//type
import type { ReactNode } from 'react';

import { Listbox } from '@headlessui/react';
import clsx from 'clsx';

import type {
    OptionType,
    SelectComponentProps
} from '@/components/base/Select/SelectComponent/types';

//components
import Icon from '@/components/base/Icon';
//config
import {
    buttonVariants,
    sizeVariants
} from '@/components/base/Select/SelectComponent/selectConfig';

//styles
const selectDisabled =
    'bg-icoGray-300 dark:text-icoGray-400 dark:bg-icoDarkMode-goudron cursor-not-allowed' as const;
const buttonBaseStyle =
    'relative font-GSemiBold w-full text-left rounded-md focus:outline-none focus:ring-offset-0 focus:ring-2 focus:ring-opacity-75' as const;
const selectButtonStyleError =
    'border border-icoRed bg-white dark:bg-icoDarkMode-anthracite focus:ring-icoRed-200' as const;

export type ButtonProps<TOption, TSelected> = Readonly<
    Pick<
        SelectComponentProps<TOption, TSelected>,
        'error' | 'disabled' | 'selected' | 'buttonVariant'
    >
> & {
    size?: SelectComponentProps<TOption, TSelected>['panelOptions']['size'];
    hasChevron?: SelectComponentProps<TOption, TSelected>['layoutOptions']['hasChevron'];
    children: ReactNode;
    className?: string;
};
export function Button<TOption extends OptionType, TSelected extends OptionType | OptionType[]>({
    error,
    disabled,
    buttonVariant = 'default',
    size = 'default',
    hasChevron = true,
    className,
    children
}: ButtonProps<TOption, TSelected>) {
    return (
        <Listbox.Button
            data-testid="select-button"
            className={clsx(
                buttonBaseStyle,
                sizeVariants[size],
                {
                    [selectButtonStyleError]: error && !disabled,
                    [buttonVariants[buttonVariant]]: !error && !disabled,
                    [selectDisabled]: disabled,
                    'px-5': !hasChevron && size === 'default',
                    'pl-5 pr-9':
                        hasChevron &&
                        size === 'default' &&
                        buttonVariant !== 'transparentBackground',
                    'px-3': !hasChevron && (size === 'xs' || size === 'sm'),
                    'pl-3 pr-7': hasChevron && (size === 'xs' || size === 'sm')
                },
                className
            )}>
            {children}
            <SelectButtonChevron disabled={disabled} hasChevron={hasChevron} size={size} />
        </Listbox.Button>
    );
}

type SelectButtonChevronProps<TOption, TSelected> = Readonly<
    Pick<SelectComponentProps<TOption, TSelected>, 'disabled'> & {
        hasChevron?: SelectComponentProps<TOption, TSelected>['layoutOptions']['hasChevron'];
        size?: SelectComponentProps<TOption, TSelected>['panelOptions']['size'];
    }
>;
function SelectButtonChevron<
    TOption extends OptionType,
    TSelected extends OptionType | OptionType[]
>({ hasChevron, size, disabled }: SelectButtonChevronProps<TOption, TSelected>) {
    if (!hasChevron) {
        return null;
    }

    return (
        <span
            className={clsx('pointer-events-none absolute inset-y-0 right-0 flex items-center', {
                'pr-5': size === 'default' || size === 'noPadding',
                'pr-3': size === 'xs' || size === 'sm',
                'p-0': size === 'noValue'
            })}>
            <Icon
                icon="chevron-bottom"
                className={clsx('text-icoGray-500', {
                    'text-xs': size === 'noValue',
                    'text-icoGray-300 dark:text-icoDarkMode-beton': disabled
                })}
            />
        </span>
    );
}
